<template>
  <div class="w-full px-4 sm:px-6 lg:px-8">
    <div class="flex items-center justify-between gap-3">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">Bookings</h1>
      </div>
      <BaseSegmentedControl
        v-if="canUseCalendar"
        v-model="view"
        :options="viewOptions"
      />
    </div>

    <BookingsCalendar v-if="showCalendar" class="mt-8" />

    <div v-show="!showCalendar" class="mt-8 flex flex-col">
      <div class="mb-4 flex w-full flex-wrap items-center justify-between">
        <ResponsiveBookingFilter
          v-model="localFilters.status"
          :options="statusFilterOptions"
        />
        <BaseDateIntervalSelect v-model="datePickerModel" />
      </div>
      <div class="mb-4 flex w-full flex-wrap items-center justify-between">
        <FilterToggle
          :model-value="bookingTypeToggleModel"
          :options="bookingTypeToggleOptions"
          @update:model-value="setBookingTypeToggleModel"
        />

        <FilterToggle
          v-model="localFilters.balance"
          :options="balanceFilterOptions"
        />
      </div>
      <div>
        <div class="mb-4 flex w-full flex-wrap justify-between gap-2">
          <BaseInputSearch v-model="localFilters.search" />
          <BasePagination v-model="page" :page-count="pageCount" />
        </div>
        <div class="mb-4 flex w-full flex-wrap justify-between gap-2">
          <BaseCountSelect
            v-model="paginationCount"
            :total-count="data?.count || 0"
          />
          <button
            type="button"
            class="text-primary border-1 border-primary relative rounded-lg px-2 py-1"
            :disable="isFetching"
            @click="() => refetch()"
          >
            refresh
            <template v-if="isFetching">
              <BaseContainerLoading spinner-class="h-4 w-4 " />
            </template>
          </button>
        </div>

        <div class="mb-4 flex w-full flex-wrap justify-between gap-2">
          <div class="flex items-center gap-2">
            <label class="text-sm">Action</label>
            <BaseInputSelect
              v-model="selectedActionId"
              :options="actionOptions"
              input-class="min-w-42"
            />
            <button
              class="bg-primary text-primary-fg rounded px-2 py-1 disabled:opacity-50"
              :disabled="
                !selectedActionId ||
                (selectedRowIds.size === 0 && !selectedAllFiltered)
              "
              @click="onGoClick"
            >
              Go
            </button>
            <template v-if="selectedRowIds.size > 0">
              <p class="text-sm text-black/60">
                {{ selectedRowIds.size }} selected of {{ rows.length }}
              </p>
            </template>
            <template v-if="isAllOnPageSelected && !selectedAllFiltered">
              <button
                class="text-primary text-sm underline disabled:opacity-50"
                :disabled="(data?.count || 0) === 0"
                @click="selectAllFiltered"
              >
                Select all {{ data?.count || 0 }} bookings
              </button>
            </template>
          </div>
        </div>
        <BaseTable
          key-field="id"
          :sort-by="localFilters.sortBy"
          :rows="rows"
          :columns="columns"
          :row-class="rowClass"
          :is-loading="isFetching"
          @click-row="onClickRow"
          @update:sort-by="onUpdateSortBy"
        >
          <template #header:select>
            <BaseInputCheckbox
              @click.stop
              @mousedown.stop
              :model-value="isAllOnPageSelected"
              :indeterminate="isSomeOnPageSelected"
              @update:model-value="toggleSelectAllOnPage"
            />
          </template>
          <template #body-cell:select="{ value }">
            <BaseInputCheckbox
              @click.stop
              @mousedown.stop
              :model-value="selectedRowIds.has(value)"
              @update:model-value="() => toggleRow(value)"
            />
          </template>
          <template #body-cell:bookingRef="{ value }">
            <div class="p-2">
              <div
                :class="[
                  'font-sm rounded-full px-4 py-2 text-center font-semibold',
                  value.bookingType === 'experience_freedom'
                    ? 'text-primary-fg bg-[#2c94a7]'
                    : 'bg-primary text-primary-fg',
                ]"
              >
                {{ value.bookingRef }}
              </div>
            </div>
          </template>

          <template #body-cell:checked_in_at="{ value: { date1, date2 } }">
            <div>
              <template v-if="date1">
                <p class="font-semibold text-black">
                  {{ formatDatetimeToDatetimeFull(date1) }}
                </p>
              </template>
              <template v-if="date2">
                <p>
                  Arrived:
                  {{ formatDatetimeToDatetimeFull(date2) }}
                </p>
              </template>
            </div>
          </template>

          <template
            #body-cell:bookingPeriod="{ value: { startDate, endDate } }"
          >
            <div>
              <p class="font-semibold text-black">
                {{ getNumberOfNightsBetweenDates(startDate, endDate) }}
                night(s)
              </p>
              <p>
                {{ formatStartToEnd(startDate, endDate) }}
              </p>
            </div>
          </template>

          <template #body-cell:balanceDue="{ value: { value, autoBalance } }">
            <div class="space-between flex justify-end space-x-2">
              <template v-if="autoBalance">
                <span
                  class="text-green border-1 border-green group relative rounded-lg px-1"
                >
                  ABF
                  <div
                    class="text-background-fg bg-background border-1 border-gray/10 invisible absolute bottom-0 -mb-6 rounded px-1 group-hover:visible"
                  >
                    Auto Balance Flag
                  </div>
                </span>
              </template>
              <span>
                {{ value }}
              </span>
            </div>
          </template>

          <template #body-cell:note="{ value }">
            <template v-if="value">
              <BookingNote :note="value" />
            </template>
          </template>

          <template #body-cell:pitch="{ value }">
            <div v-if="value.storage">
              <p class="font-semibold text-black">
                {{ value.storage.pitchName }}
              </p>
              <StorageStateChip
                class="mt-1 inline-block"
                :state="
                  storageMarkerState({
                    location: value.storage.location,
                    ended: value.ended,
                  })
                "
              />
            </div>
            <div v-else-if="value.bookingType !== 'experience_freedom'">
              <template v-if="value.pitchNumber">
                <p class="font-semibold text-black">
                  {{ value.pitchNumber }}
                </p>
              </template>
              <template v-else>
                <a
                  v-if="!value.reservedName"
                  class="text-primary cursor-pointer underline"
                >
                  Add Pitch
                </a>
                <p v-else class="text-xs font-semibold text-amber-600">
                  Reserved: {{ value.reservedName }}
                </p>
              </template>
              <p>
                {{ value.pitchType }}
              </p>
            </div>
            <div v-else>
              <p class="font-semibold text-black">
                {{ value.pitchName }}
              </p>
              <p>
                {{ value.pitchType }}
              </p>
            </div>
          </template>

          <template #body-cell:customer_name="{ value }">
            <CustomerItem
              :customer="value.customer"
              :is-companion="value.isCompanion"
            />
          </template>

          <template #body-cell:vehicles="{ value }">
            <span>
              <p class="text-md font-bold uppercase">
                {{ value.primaryVehicleBooking?.registration }}
              </p>
              <div class="divide-x-1 -mx-1">
                <template
                  v-for="vehicleBooking of value.vehicleBookings"
                  :key="vehicleBooking.vehicleRegistation"
                >
                  <span class="px-1 uppercase">
                    {{ vehicleBooking.registration }}
                  </span>
                </template>
              </div>
            </span>
          </template>

          <template #body-cell:status="{ value }">
            <BookingStatusChip :status="value" />
          </template>

          <template #body-cell:alert="{ value }">
            <template v-if="value">
              <IconHeroiconsSolidExclamationCircle class="text-error h-6 w-6" />
            </template>
            <template v-else>
              <div class="h-6 w-6" />
            </template>
          </template>
        </BaseTable>
      </div>
    </div>
  </div>

  <!-- Shows the booking modal -->
  <RouterView :key="$route.fullPath" @close="onModalClose" />
  <BaseConfirmation
    ref="arriveConfirmRef"
    title="Confirm action"
    description="Mark selected bookings as arrived?"
    confirm-label="Run"
    cancel-label="Cancel"
    type="negative"
  />
  <BaseConfirmation
    ref="departConfirmRef"
    title="Confirm action"
    description="Mark selected bookings as departed? Only bookings that have arrived will be updated."
    confirm-label="Run"
    cancel-label="Cancel"
    type="negative"
  />
  <BaseConfirmation
    ref="cancelConfirmRef"
    title="Confirm action"
    description="Cancel selected bookings?"
    confirm-label="Run"
    cancel-label="Cancel"
    type="negative"
  />
  <BaseConfirmation
    ref="deleteConfirmRef"
    title="Confirm action"
    description="Delete selected bookings? This cannot be undone."
    confirm-label="Run"
    cancel-label="Cancel"
    type="negative"
  />

  <BaseConfirmation
    ref="companionLinkConfirmRef"
    title="Confirm action"
    description="Text selected bookings a fresh Arrival Companion login link? Bookings away from a companion site, Experience Freedom bookings, and those with no phone number are skipped."
    confirm-label="Run"
    cancel-label="Cancel"
    type="negative"
  />

  <SendMessageConfirmation
    ref="sendMessageConfirmRef"
    title="Send bulk message"
    confirm-label="Send"
    cancel-label="Cancel"
  />
</template>

<script setup lang="ts">
import {
  type Booking,
  isBookingDisallowed,
} from "@/services/bookings/bookings";
import { storageMarkerState } from "@/services/storage/storage";
import { type BookingType } from "@/services/bookings/bookingTypes";
import { type Customer } from "@/services/bookings/customers";
import {
  type BookingStatus,
  isListBookingsSortbyOption,
  type ListBookingsQuery,
  type ListBookingsSortbyOption,
} from "@/services/bookings/listBookings";
import {
  useHasExitCamera,
  useHasBulkMessage,
  useIsCompanion,
} from "@/services/locations/useCurrentSite";
import { useSiteColumn } from "@/services/locations/useSiteColumn";
import { type BaseRow, type ColumnDefinition } from "@/types/table";
import { api } from "@/api";
import { useCurrentUser } from "@/composables/useCurrentUser";
import BaseConfirmation from "@/components/BaseConfirmation.vue";
import SendMessageConfirmation from "@/components/SendMessageConfirmation.vue";
import ResponsiveBookingFilter from "@/components/ResponsiveBookingFilter.vue";

const router = useRouter();

// List vs Calendar view toggle (mirrors the Site Plans [Cards | Map] control).
// Persisted so a page reload keeps the chosen view instead of snapping to List.
const view = useStorage<"list" | "calendar">("camc.bookings-view", "list");
const viewOptions = [
  { label: "List", value: "list" },
  { label: "Calendar", value: "calendar" },
];

// The Calendar is still being trialled — expose it only to the admin account
// on the staging host. Prod (camc.visitor.express) and everyone else see the
// list only. showCalendar also gates the render so a stale toggle state can't
// reveal it.
const { currentUser } = useCurrentUser();
const canUseCalendar = computed(
  () =>
    currentUser.value?.email === "admin@netfm.org" &&
    window.location.hostname.includes("staging"),
);
const showCalendar = computed(
  () => canUseCalendar.value && view.value === "calendar",
);

const { options: bookingTypeOptions } = useBookingTypes();

const bookingTypeToggleOptions = computed(() => bookingTypeOptions.value);
// null means "no explicit selection yet", so every available type stays checked.
// The options depend on the sites query, which resolves after setup on a reload —
// snapshotting them into the ref up front would leave late-arriving types unchecked.
const bookingTypeToggleModelLocal = ref<BookingType[] | null>(null);
const bookingTypeToggleModel = computed<BookingType[]>(
  () =>
    bookingTypeToggleModelLocal.value ??
    bookingTypeToggleOptions.value.map(({ value }) => value),
);
const setBookingTypeToggleModel = (newValue: BookingType[]) => {
  bookingTypeToggleModelLocal.value = newValue;
};

const bookingTypeFilters = computed(() => ({
  include_booking_types: bookingTypeToggleModel.value,
}));

const { siteColumns } = useSiteColumn<Booking>((row) => row.siteId);

const columns = computed<ColumnDefinition<Booking>[]>(() => [
  {
    label: "",
    name: "select",
    autoWidth: true,
    getValue: (row) => row.id,
    align: "center",
  },
  {
    label: "",
    name: "alert",
    autoWidth: true,
    getValue: (row) => row.hasAlert,
    align: "center",
  },
  {
    label: "Booking Ref.",
    name: "bookingRef",
    autoWidth: true,
    getValue: (row) => ({
      bookingType: row.bookingType,
      bookingRef: row.bookingRef,
    }),
    align: "center",
  },

  {
    label: "Balance Due",
    name: "balanceDue",
    autoWidth: true,
    align: "right",
    getValue: (row) => ({
      value: formatToCurrencyString(row.balanceDue),
      autoBalance: row.autoBalance,
    }),
    isSortable: true,
  },

  {
    label: "Customer",
    name: "customer_name",
    getValue: (row) => ({
      customer: row.customer,
      isCompanion: row.isCompanion,
    }),
    isSortable: true,
  },

  ...siteColumns.value,

  {
    label: "Status",
    name: "status",
    getValue: (row) => row.status,
  },

  {
    label:
      localFilters.status == "arrived"
        ? "Arrived Time"
        : localFilters.status == "departed"
          ? localFilters.sortBy == "-checked_in_at"
            ? "Arrival Time"
            : "Departed Time"
          : localFilters.status == "overstayed"
            ? localFilters.sortBy == "-checked_in_at"
              ? "Arrival Time"
              : "Overstaying From"
            : localFilters.sortBy == "-checked_in_at"
              ? "Arrival Time"
              : "Checked-in Time",
    name: "checked_in_at",
    getValue: (row) => {
      return localFilters.status == "arrived"
        ? {
            date1: row.arrivedAt && DateFns.parseISO(row.arrivedAt),
            date2: undefined,
          }
        : localFilters.status == "departed"
          ? {
              date1: row.departedAt && DateFns.parseISO(row.departedAt),
              date2: row.arrivedAt && DateFns.parseISO(row.arrivedAt),
            }
          : localFilters.status == "overstayed"
            ? {
                date1:
                  row.departureEnd &&
                  DateFns.parseISO(`${row.endDate} ${row.departureEnd}`),
                date2: row.arrivedAt && DateFns.parseISO(row.arrivedAt),
              }
            : {
                date1: row.checkedInAt && DateFns.parseISO(row.checkedInAt),
                date2: row.arrivedAt && DateFns.parseISO(row.arrivedAt),
              };
    },
    isSortable: true,
  },

  {
    label: "Booking Period",
    name: "bookingPeriod",
    getValue: (row) => ({
      startDate: DateFns.parseISO(row.startDate),
      endDate: DateFns.parseISO(row.endDate),
    }),
  },

  {
    label: "Pitch/Unit",
    name: "pitch",
    getValue: (row) =>
      row.storage
        ? {
            storage: row.storage,
            ended: row.endDate < DateFns.format(new Date(), "yyyy-MM-dd"),
          }
        : row.bookingType == "experience_freedom"
          ? {
              bookingType: row.bookingType,
              pitchName: row.pitch?.name ?? null,
              pitchType: row.pitchType,
            }
          : {
              bookingType: row.bookingType,
              pitchType: row.pitchType,
              pitchNumber: row.pitch?.name || row.pitchNumber,
              reservedName: row.reserved?.name ?? null,
            },
    align: "center",
    isSortable: true,
  },

  {
    label: "Vehicles",
    name: "vehicles",
    getValue: (row) => ({
      primaryVehicleBooking: row.primaryVehicleBooking,
      vehicleBookings: row.vehicleBookings,
    }),
  },

  {
    label: "Note",
    name: "note",
    getValue: (row) => row.note,
  },
]);

const getNumberOfNightsBetweenDates = (date1: Date, date2: Date): number =>
  Math.abs(DateFns.differenceInCalendarDays(date1, date2));

const formatStartToEnd = (start: Date, end: Date): string =>
  `${formatDatetimeToDatePart(start)} - ${formatDatetimeToDatePart(end)}`;

const onClickRow = (row: BaseRow) => {
  if (row.status === "deleted") {
    return;
  }

  router.push({
    name: "bookingInstance",
    params: {
      bookingId: row.id,
    },
  });
};

const onModalClose = () => {
  router.push({
    name: "bookings",
  });
};

const localFilters = reactive<{
  status: BookingStatus | null;
  sortBy: ListBookingsSortbyOption | null;
  search: string;
  balance: ("zero" | "outstanding" | "abf")[];
}>({
  status: null,
  sortBy: null,
  search: "",
  balance: [],
});

const { datePickerModel } = useDatePickerContext();
const filters = reactiveComputed(() => ({
  ...localFilters,
  ...bookingTypeFilters.value,
  ...{
    fromDt: datePickerModel.value.start,
    untilDt: datePickerModel.value.end,
  },
}));
const hasExitCamera = useHasExitCamera();

const statusFilterOptions = computed(() => {
  const baseOptions = [
    {
      label: "All",
      value: null,
    },
    {
      label: "Scheduled",
      value: "scheduled",
    },
    {
      label: "Expected",
      value: "expected",
    },
    {
      label: "Arrived",
      value: "arrived",
    },
    {
      label: "Checked In",
      value: "checkedin",
    },
    {
      label: "No Show",
      value: "noshow",
    },
    {
      label: "Cancelled",
      value: "cancelled",
    },
  ];

  if (hasExitCamera.value) {
    return [
      ...baseOptions,
      {
        label: "Departed",
        value: "departed",
      },
      {
        label: "Over-stayed",
        value: "overstayed",
      },
      {
        label: "Disallowed",
        value: "disallowed",
      },
    ];
  }

  return baseOptions;
});

const balanceFilterOptions = [
  {
    label: "Zero Balance",
    value: "zero",
  },
  {
    label: "Outstanding Balance",
    value: "outstanding",
  },
  {
    label: "ABF",
    value: "abf",
  },
];

const paginationCount = ref(100);

const { pagination, page, pageCount, resetPagination } = usePagination({
  limit: paginationCount,
  count: computed(() => data.value?.count || 0),
});

watch(
  () => filters,
  () => resetPagination(),
  { deep: true, immediate: true },
);

const query = reactiveComputed<ListBookingsQuery>(() => ({
  ...filters,
  ...pagination.value,
}));

const onUpdateSortBy = (value: string | null) => {
  if (value === null || isListBookingsSortbyOption(value)) {
    localFilters.sortBy = value;
  }
};

const { data, isFetching, refetch } = useBookings(query);

const rows = computed(() => data.value?.items ?? []);
// Red: the barrier will not open for this booking (Disallow List or checkbox).
const rowClass = (row: Booking) =>
  isBookingDisallowed(row)
    ? "cursor-pointer bg-red bg-opacity-10"
    : "cursor-pointer";

const selectedRowIds = reactive(new Set<string>());
const isAllOnPageSelected = computed(
  () =>
    rows.value.length > 0 && rows.value.every((r) => selectedRowIds.has(r.id)),
);
const isSomeOnPageSelected = computed(
  () =>
    rows.value.length > 0 &&
    rows.value.some((r) => selectedRowIds.has(r.id)) &&
    !isAllOnPageSelected.value,
);
const toggleRow = (id: string) => {
  if (selectedRowIds.has(id)) selectedRowIds.delete(id);
  else selectedRowIds.add(id);
};
const toggleSelectAllOnPage = () => {
  if (isAllOnPageSelected.value) {
    rows.value.forEach((r) => selectedRowIds.delete(r.id));
  } else {
    rows.value.forEach((r) => selectedRowIds.add(r.id));
  }
};

const selectedActionId = ref<
  | "arrive"
  | "depart"
  | "cancel"
  | "delete"
  | "message"
  | "companion-link"
  | null
>(null);
const hasBulkMessage = useHasBulkMessage();
const isCompanion = useIsCompanion();
const actionOptions = computed(() => {
  const options = [
    { value: "arrive", label: "Set as Arrived" },
    { value: "cancel", label: "Cancel" },
    { value: "delete", label: "Delete" },
  ];
  if (hasExitCamera.value) {
    options.push({ value: "depart", label: "Mark as departed" });
  }
  if (hasBulkMessage.value) {
    options.push({ value: "message", label: "Send Message" });
  }
  // True for "All sites" too, where the backend skips the non-companion
  // bookings in the selection rather than the option disappearing.
  if (isCompanion.value) {
    options.push({ value: "companion-link", label: "Send Companion Link" });
  }
  return options;
});
const { id: currentSiteId } = useCurrentSite();
const selectedAllFiltered = ref(false);
const selectAllFiltered = () => {
  selectedAllFiltered.value = true;
};

// Bulk actions take explicit booking ids only, at most this many at once, so
// "Select all N bookings" is resolved to the ids the list's filters match first.
const MAX_BULK_BOOKINGS = 500;
const selectedBookingIds = async (): Promise<string[] | null> => {
  if (!selectedAllFiltered.value) return Array.from(selectedRowIds);
  const { body } = await api.get<string[]>("/bookings/ids", {
    query: { ...filters, siteId: currentSiteId.value },
  });
  if (body.length > MAX_BULK_BOOKINGS) {
    toast.error(
      `Bulk actions are limited to ${MAX_BULK_BOOKINGS} bookings at once. Narrow the filters and try again.`,
    );
    return null;
  }
  return body;
};

const runArrive = async () => {
  const booking_ids = await selectedBookingIds();
  if (booking_ids === null) return;
  const payload = { booking_ids };
  try {
    await api.post("/bookings/bulk/arrive", { body: payload });
    toast.success("Set as Arrived completed");
    await refetch();
    if (!selectedAllFiltered.value) selectedRowIds.clear();
    selectedAllFiltered.value = false;
  } catch (e) {
    toast.error("Bulk action failed");
    throw e;
  }
};

const runDepart = async () => {
  const booking_ids = await selectedBookingIds();
  if (booking_ids === null) return;
  const payload = { booking_ids };
  try {
    await api.post("/bookings/bulk/depart", { body: payload });
    toast.success("Mark as departed completed");
    await refetch();
    if (!selectedAllFiltered.value) selectedRowIds.clear();
    selectedAllFiltered.value = false;
  } catch (e) {
    toast.error("Bulk action failed");
    throw e;
  }
};

const runCancel = async () => {
  const booking_ids = await selectedBookingIds();
  if (booking_ids === null) return;
  const payload = { booking_ids };
  try {
    await api.post("/bookings/bulk/cancel", { body: payload });
    toast.success("Cancel completed");
    await refetch();
    if (!selectedAllFiltered.value) selectedRowIds.clear();
    selectedAllFiltered.value = false;
  } catch (e) {
    toast.error("Bulk action failed");
    throw e;
  }
};

const runDelete = async () => {
  const booking_ids = await selectedBookingIds();
  if (booking_ids === null) return;
  const payload = { booking_ids };
  try {
    await api.post("/bookings/bulk/delete", { body: payload });
    toast.success("Delete completed");
    await refetch();
    if (!selectedAllFiltered.value) selectedRowIds.clear();
    selectedAllFiltered.value = false;
  } catch (e) {
    toast.error("Bulk action failed");
    throw e;
  }
};

const runCompanionLink = async () => {
  const booking_ids = await selectedBookingIds();
  if (booking_ids === null) return;
  const payload = { booking_ids };
  try {
    const { body } = await api.post<{ count: number }>(
      "/bookings/bulk/companion-link",
      { body: payload },
    );
    // Bookings away from a companion site, or without a phone number, are
    // skipped — report what actually went out rather than what was selected.
    toast.success(`Companion link sent to ${body.count} booking(s)`);
    await refetch();
    if (!selectedAllFiltered.value) selectedRowIds.clear();
    selectedAllFiltered.value = false;
  } catch (e) {
    toast.error("Bulk action failed");
    throw e;
  }
};

const arriveConfirmRef = ref<InstanceType<typeof BaseConfirmation> | null>(
  null,
);
const departConfirmRef = ref<InstanceType<typeof BaseConfirmation> | null>(
  null,
);
const cancelConfirmRef = ref<InstanceType<typeof BaseConfirmation> | null>(
  null,
);
const deleteConfirmRef = ref<InstanceType<typeof BaseConfirmation> | null>(
  null,
);
const companionLinkConfirmRef = ref<InstanceType<
  typeof BaseConfirmation
> | null>(null);
const sendMessageConfirmRef = ref<InstanceType<
  typeof SendMessageConfirmation
> | null>(null);

const onGoClick = async () => {
  if (selectedActionId.value === "arrive") {
    const { isCanceled } = await arriveConfirmRef.value!.reveal();
    if (!isCanceled) await runArrive();
  } else if (selectedActionId.value === "depart") {
    const { isCanceled } = await departConfirmRef.value!.reveal();
    if (!isCanceled) await runDepart();
  } else if (selectedActionId.value === "cancel") {
    const { isCanceled } = await cancelConfirmRef.value!.reveal();
    if (!isCanceled) await runCancel();
  } else if (selectedActionId.value === "delete") {
    const { isCanceled } = await deleteConfirmRef.value!.reveal();
    if (!isCanceled) await runDelete();
  } else if (selectedActionId.value === "message") {
    const { isCanceled, data } = await sendMessageConfirmRef.value!.reveal();
    if (!isCanceled) await runMessage(String(data ?? ""));
  } else if (selectedActionId.value === "companion-link") {
    const { isCanceled } = await companionLinkConfirmRef.value!.reveal();
    if (!isCanceled) await runCompanionLink();
  }
};

const sortByState = reactive<
  Map<BookingStatus | null, ListBookingsSortbyOption | null>
>(
  new Map([
    [null, null],
    ["arrived", "-checked_in_at"],
    ["checkedin", "-checked_in_at"],
    ["expected", null],
    ["noshow", null],
    ["scheduled", null],
    ["unknown", null],
    ["cancelled", null],
    ["departed", "-checked_in_at"],
    ["overstayed", "checked_in_at"],
    ["disallowed", "checked_in_at"],
  ]),
);

watch(
  () => filters.status,
  (value) => {
    localFilters.sortBy = sortByState.get(value) ?? null;
  },
);

watch(
  () => filters.sortBy,
  (value) => {
    sortByState.set(filters.status, value);
  },
);

const runMessage = async (message: string) => {
  if (!message) return;

  const booking_ids = await selectedBookingIds();
  if (booking_ids === null) return;
  const selection = { booking_ids };

  try {
    await api.post("/bookings/bulk/message", {
      body: { selection, content: message },
    });
    toast.success("Bulk message sent");
    await refetch();
    if (!selectedAllFiltered.value) selectedRowIds.clear();
    selectedAllFiltered.value = false;
  } catch (e) {
    toast.error("Bulk action failed");
    throw e;
  }
};
</script>
